import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminPageHeader from '../../common/AdminPageHeader';
import { useQueryClient } from '@tanstack/react-query';
import Pagination from './UserTablePagination';
import { User, useUserColumns, useUsersData } from './TableData';
import { SearchIcon } from '../../../svg';
import {
  deleteUser,
  whitelistUser,
  getAdminAnalytics,
  fetchAllUsersAndLinks,
} from '../../../services/userService';
import { toast } from 'react-toastify';
import ConfirmModal from '../../common/ConfirmModal';
import useI18n from '../../../hooks/useI18n';
import {
  ADMIN_METRIC_ACCENT_TEXT,
  ADMIN_TEXT_MUTED_HOVER_ACCENT,
} from '@/config/adminChartTheme';
import useDebounce from '../../../hooks/useDebounce';
import UserStatsCards from './UserStatsCards';
import UserActionsMenu from './UserActionsMenu';
import EmptyUsersState from './EmptyUsersState';

const UsersTable: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [userFilter, setUserFilter] = useState('');
  const debouncedFilter = useDebounce(userFilter, 500);
  const [userPage, setUserPage] = useState(1);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [whitelistFilter, setWhitelistFilter] = useState<string>('all');
  const entriesPerPage = 15;

  // Global stats (independent of filters/pagination)
  const [globalStats, setGlobalStats] = useState({
    totalUsers: 0,
    totalLinks: 0,
    whitelistedUsers: 0,
    totalClicks: 0,
    loading: true,
  });

  const {
    users,
    totalUsers,
    loading: usersLoading,
    error: usersError,
    fetchUsers,
  } = useUsersData(
    debouncedFilter,
    userPage,
    entriesPerPage,
    sortField,
    sortOrder,
  );

  // Fetch global stats
  useEffect(() => {
    const fetchGlobalStats = async () => {
      try {
        const analytics = await getAdminAnalytics();

        // If API doesn't return whitelistedUsers, we need to count from all users
        let whitelistedCount = parseInt(
          analytics?.whitelistedUsers ||
            analytics?.whitelisted ||
            analytics?.whitelistCount ||
            '0',
        );

        // If still 0, try to fetch from all users
        if (whitelistedCount === 0) {
          try {
            const allUsers = await fetchAllUsersAndLinks({
              filter: '',
              page: 1,
              limit: 10000, // Get all users
              sortField: 'createdAt',
              sortOrder: 'desc',
            });
            whitelistedCount =
              allUsers?.data?.filter((u: User) => u.isWhitelisted).length || 0;
          } catch (e) {
            console.error('Failed to count whitelisted users:', e);
          }
        }

        setGlobalStats({
          totalUsers: parseInt(analytics?.users || '0'),
          totalLinks: parseInt(analytics?.links || '0'),
          whitelistedUsers: whitelistedCount,
          totalClicks: parseInt(analytics?.clicks || '0'),
          loading: false,
        });
      } catch (error) {
        console.error('Failed to fetch global stats:', error);
        setGlobalStats({
          totalUsers: 0,
          totalLinks: 0,
          whitelistedUsers: 0,
          totalClicks: 0,
          loading: false,
        });
      }
    };
    fetchGlobalStats();
  }, []);

  // Refetch global stats after user operations
  const refetchGlobalStats = async () => {
    try {
      const analytics = await getAdminAnalytics();

      // If API doesn't return whitelistedUsers, count from all users
      let whitelistedCount = parseInt(
        analytics?.whitelistedUsers ||
          analytics?.whitelisted ||
          analytics?.whitelistCount ||
          '0',
      );

      if (whitelistedCount === 0) {
        try {
          const allUsers = await fetchAllUsersAndLinks({
            filter: '',
            page: 1,
            limit: 10000,
            sortField: 'createdAt',
            sortOrder: 'desc',
          });
          whitelistedCount =
            allUsers?.data?.filter((u: User) => u.isWhitelisted).length || 0;
        } catch (e) {
          console.error('Failed to count whitelisted users:', e);
        }
      }

      setGlobalStats({
        totalUsers: parseInt(analytics?.users || '0'),
        totalLinks: parseInt(analytics?.links || '0'),
        whitelistedUsers: whitelistedCount,
        totalClicks: parseInt(analytics?.clicks || '0'),
        loading: false,
      });
    } catch (error) {
      console.error('Failed to fetch global stats:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await deleteUser(userId);
      toast.success('User deleted successfully!');
      fetchUsers();
      refetchGlobalStats();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const confirmDeleteUser = (user: User): void => {
    setUserToDelete(user);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (userToDelete) {
      await handleDeleteUser(userToDelete._id);
      setShowConfirmModal(false);
      setUserToDelete(null);
    }
  };

  const handleWhitelist = async (userId: string) => {
    try {
      const updatedUser = await whitelistUser(userId);
      if (updatedUser && updatedUser.user) {
        toast.success(
          updatedUser.user.isWhitelisted
            ? 'User whitelisted successfully!'
            : 'User removed from whitelist successfully!',
        );
        fetchUsers();
        refetchGlobalStats();
      }
    } catch (error) {
      toast.error('Failed to update whitelist status');
    }
  };

  const userColumns = useUserColumns();

  const handleSort = (field: string) => {
    const newSortOrder =
      sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newSortOrder);
  };

  const handleUserSelect = (user: User) => {
    navigate(`/admin/users/${user._id}`);
  };

  // Filter users based on status, role, and whitelist
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      if (statusFilter !== 'all' && user.status !== statusFilter) return false;
      if (roleFilter !== 'all' && user.role !== roleFilter) return false;
      if (whitelistFilter === 'whitelisted' && !user.isWhitelisted)
        return false;
      if (whitelistFilter === 'not-whitelisted' && user.isWhitelisted)
        return false;
      return true;
    });
  }, [users, statusFilter, roleFilter, whitelistFilter]);

  const hasActiveFilters =
    statusFilter !== 'all' ||
    roleFilter !== 'all' ||
    whitelistFilter !== 'all' ||
    userFilter.trim() !== '';

  const clearAllFilters = () => {
    setUserFilter('');
    setStatusFilter('all');
    setRoleFilter('all');
    setWhitelistFilter('all');
  };

  return (
    <div className="bg-[#FCFCFC] min-h-screen">
      <AdminPageHeader
        title={String(t('components.users'))}
        subtitle="Manage and monitor all users on your platform"
      />

      <div className="p-[24px]">
        {/* Statistics Cards */}
        <UserStatsCards
          totalUsers={globalStats.totalUsers}
          totalLinks={globalStats.totalLinks}
          whitelistedUsers={globalStats.whitelistedUsers}
          totalClicks={globalStats.totalClicks}
          loading={globalStats.loading}
        />

        {/* Filters and Search */}
        <div className="bg-white border border-[#E5E9EB] rounded-3xl p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Search Bar */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email..."
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-[#E5E9EB] rounded-2xl outline-none focus:border-[#3C2479] transition-colors"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-[106px] px-4 py-2 border border-[#E5E9EB] rounded-xl text-sm outline-none focus:border-[#3C2479] transition-colors cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
                <option value="blocked">Blocked</option>
              </select>

              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-[118px] appearance-none px-4 py-2 border border-[#E5E9EB] rounded-xl text-sm outline-none focus:border-[#3C2479] transition-colors cursor-pointer bg-white truncate"
              >
                <option value="all">All Roles</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="moderator">Moderator</option>
              </select>

              <select
                value={whitelistFilter}
                onChange={(e) => setWhitelistFilter(e.target.value)}
                className="w-[145px] appearance-none px-4 py-2 border border-[#E5E9EB] rounded-xl text-sm outline-none focus:border-[#3C2479] transition-colors cursor-pointer bg-white truncate"
              >
                <option value="all">All Users</option>
                <option value="whitelisted">Whitelisted</option>
                <option value="not-whitelisted">Not Whitelisted</option>
              </select>

              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className={`px-4 py-2 text-sm transition-colors ${ADMIN_TEXT_MUTED_HOVER_ACCENT}`}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Error State */}
        {usersError && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6">
            <p className="text-red-600 text-sm">{usersError}</p>
          </div>
        )}

        {/* Loading State */}
        {usersLoading && users.length === 0 ? (
          <div className="bg-white border border-[#E5E9EB] rounded-3xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-[#E5E9EB]">
                    {userColumns.map((column) => (
                      <th
                        key={column.key}
                        className="px-6 py-4 text-left text-xs font-semibold text-[#6B778C] uppercase tracking-wider"
                        style={{ width: column.width }}
                      >
                        {column.title}
                      </th>
                    ))}
                    <th className="px-6 py-4 text-center text-xs font-semibold text-[#6B778C] uppercase tracking-wider w-[5%]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E9EB]">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-[40px] h-[40px] bg-gray-200 rounded-full" />
                          <div className="flex-1">
                            <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                            <div className="h-3 bg-gray-200 rounded w-40" />
                          </div>
                        </div>
                      </td>
                      {[...Array(7)].map((_, idx) => (
                        <td key={idx} className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-20 mx-auto" />
                        </td>
                      ))}
                      <td className="px-6 py-4">
                        <div className="h-8 bg-gray-200 rounded w-8 mx-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : filteredUsers.length === 0 ? (
          /* Empty State */
          <div className="bg-white border border-[#E5E9EB] rounded-3xl">
            <EmptyUsersState
              hasFilters={hasActiveFilters}
              onClearFilters={clearAllFilters}
            />
          </div>
        ) : (
          /* Data Table */
          <>
            <div className="bg-white border border-[#E5E9EB] rounded-3xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-[#E5E9EB]">
                      {userColumns.map((column) => (
                        <th
                          key={column.key}
                          className="px-6 py-4 text-xs font-semibold text-[#6B778C] uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                          style={{
                            width: column.width,
                            textAlign:
                              column.headerAlign as React.CSSProperties['textAlign'],
                          }}
                          onClick={() => handleSort(column.key)}
                        >
                          <div className="flex items-center justify-center gap-2">
                            {column.title}
                            {sortField === column.key && (
                              <span className={ADMIN_METRIC_ACCENT_TEXT}>
                                {sortOrder === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                      <th className="px-6 py-4 text-center text-xs font-semibold text-[#6B778C] uppercase tracking-wider w-[5%]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E9EB] bg-white">
                    {filteredUsers.map((user) => (
                      <tr
                        key={user._id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleUserSelect(user)}
                      >
                        {userColumns.map((column) => (
                          <td
                            key={`${user._id}-${column.key}`}
                            className="px-6 py-4 whitespace-nowrap"
                            style={{
                              textAlign:
                                column.cellAlign as React.CSSProperties['textAlign'],
                              width: column.width,
                            }}
                          >
                            {column.render
                              ? column.render(
                                  (user as any)[column.dataIndex],
                                  user,
                                )
                              : (user as any)[column.dataIndex]}
                          </td>
                        ))}
                        <td
                          className="px-6 py-4 whitespace-nowrap text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <UserActionsMenu
                            user={user}
                            onDelete={confirmDeleteUser}
                            onWhitelist={handleWhitelist}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="mt-6">
              <Pagination
                totalItems={totalUsers}
                itemsPerPage={entriesPerPage}
                currentPage={userPage}
                onPageChange={setUserPage}
                limit={{
                  start: (userPage - 1) * entriesPerPage + 1,
                  end: Math.min(userPage * entriesPerPage, totalUsers),
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onRequestClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to delete user ${userToDelete?.email}? This action cannot be undone.`}
      />
    </div>
  );
};

export default UsersTable;
